/**
 * Enhanced SEO Components
 * 
 * Poskytuje SEO meta tagy, JSON-LD schémy a utility pre SEO.
 */

import { useEffect } from "react";

// ============================================
// CONSTANTS
// ============================================

export const SEO_DEFAULTS = {
  siteName: "Merch.sk",
  siteUrl: "https://merch.sk",
  title: "Merch.sk - Oficiálny merch slovenských interpretov",
  description: "Nakupujte oficiálny merchandise vašich obľúbených slovenských a českých interpretov. Tričká, mikiny, doplnky a viac.",
  image: "https://merch.sk/og-image.jpg",
  locale: "sk_SK",
  currency: "EUR",
  twitter: "@merchsk",
} as const;

// ============================================
// TYPES
// ============================================

interface SEOHeadProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: "website" | "product" | "article";
  price?: number;
  currency?: string;
  availability?: "in stock" | "out of stock" | "preorder";
  brand?: string;
  category?: string;
  noindex?: boolean;
  publishedTime?: string;
  modifiedTime?: string;
}

interface ProductSchemaProps {
  name: string;
  description: string;
  image: string | string[];
  price: number;
  currency?: string;
  availability?: "InStock" | "OutOfStock" | "PreOrder";
  brand?: string;
  sku?: string;
  url: string;
  reviewCount?: number;
  ratingValue?: number;
}

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface FAQItem {
  question: string;
  answer: string;
}

// ============================================
// SEO HEAD COMPONENT
// ============================================

export const SEOHead = ({
  title,
  description = SEO_DEFAULTS.description,
  image = SEO_DEFAULTS.image,
  url,
  type = "website",
  price,
  currency = SEO_DEFAULTS.currency,
  availability = "in stock",
  brand,
  category,
  noindex = false,
  publishedTime,
  modifiedTime,
}: SEOHeadProps) => {
  const fullTitle = title 
    ? `${title} | ${SEO_DEFAULTS.siteName}` 
    : SEO_DEFAULTS.title;
  const canonicalUrl = url 
    ? `${SEO_DEFAULTS.siteUrl}${url}` 
    : SEO_DEFAULTS.siteUrl;

  useEffect(() => {
    // Update document title
    document.title = fullTitle;

    // Helper to update or create meta tag
    const updateMeta = (selector: string, content: string) => {
      let element = document.querySelector(selector) as HTMLMetaElement;
      if (!element) {
        element = document.createElement("meta");
        const match = selector.match(/\[(.*?)="(.*?)"\]/);
        if (match) {
          element.setAttribute(match[1], match[2]);
        }
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
    };

    // Basic meta
    updateMeta('meta[name="description"]', description);
    
    if (noindex) {
      updateMeta('meta[name="robots"]', "noindex, nofollow");
    } else {
      updateMeta('meta[name="robots"]', "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");
    }

    // Open Graph
    updateMeta('meta[property="og:title"]', fullTitle);
    updateMeta('meta[property="og:description"]', description);
    updateMeta('meta[property="og:image"]', image);
    updateMeta('meta[property="og:url"]', canonicalUrl);
    updateMeta('meta[property="og:type"]', type);
    updateMeta('meta[property="og:site_name"]', SEO_DEFAULTS.siteName);
    updateMeta('meta[property="og:locale"]', SEO_DEFAULTS.locale);

    // Article timestamps
    if (type === "article") {
      if (publishedTime) {
        updateMeta('meta[property="article:published_time"]', publishedTime);
      }
      if (modifiedTime) {
        updateMeta('meta[property="article:modified_time"]', modifiedTime);
      }
    }

    // Twitter Card
    updateMeta('meta[name="twitter:card"]', "summary_large_image");
    updateMeta('meta[name="twitter:site"]', SEO_DEFAULTS.twitter);
    updateMeta('meta[name="twitter:title"]', fullTitle);
    updateMeta('meta[name="twitter:description"]', description);
    updateMeta('meta[name="twitter:image"]', image);

    // Product specific
    if (type === "product" && price !== undefined) {
      updateMeta('meta[property="product:price:amount"]', price.toString());
      updateMeta('meta[property="product:price:currency"]', currency);
      updateMeta('meta[property="product:availability"]', availability);
      if (brand) updateMeta('meta[property="product:brand"]', brand);
      if (category) updateMeta('meta[property="product:category"]', category);
    }

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;

  }, [fullTitle, description, image, canonicalUrl, type, price, currency, availability, brand, category, noindex, publishedTime, modifiedTime]);

  return null;
};

// ============================================
// JSON-LD SCHEMA COMPONENTS
// ============================================

/**
 * Organization Schema - pre homepage
 */
export const OrganizationSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SEO_DEFAULTS.siteName,
    url: SEO_DEFAULTS.siteUrl,
    logo: `${SEO_DEFAULTS.siteUrl}/logos/merch-logo.png`,
    sameAs: [
      "https://www.facebook.com/merchsk",
      "https://www.instagram.com/merch.sk",
      "https://www.youtube.com/@merchsk",
    ],
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+421-900-123-456",
      contactType: "customer service",
      availableLanguage: ["Slovak", "Czech", "English"],
      areaServed: ["SK", "CZ"],
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

/**
 * WebSite Schema - pre vyhľadávanie
 */
export const WebSiteSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SEO_DEFAULTS.siteName,
    url: SEO_DEFAULTS.siteUrl,
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SEO_DEFAULTS.siteUrl}/hladanie?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

/**
 * Product Schema - pre produktové stránky
 */
export const ProductSchema = ({
  name,
  description,
  image,
  price,
  currency = SEO_DEFAULTS.currency,
  availability = "InStock",
  brand,
  sku,
  url,
  reviewCount,
  ratingValue,
}: ProductSchemaProps) => {
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Product",
    name,
    description,
    image: Array.isArray(image) ? image : [image],
    sku,
    brand: brand ? { "@type": "Brand", name: brand } : undefined,
    offers: {
      "@type": "Offer",
      price,
      priceCurrency: currency,
      availability: `https://schema.org/${availability}`,
      url: `${SEO_DEFAULTS.siteUrl}${url}`,
      seller: {
        "@type": "Organization",
        name: SEO_DEFAULTS.siteName,
      },
    },
  };

  // Add aggregate rating if available
  if (reviewCount && ratingValue) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue,
      reviewCount,
      bestRating: 5,
      worstRating: 1,
    };
  }

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

/**
 * Breadcrumb Schema
 */
export const BreadcrumbSchema = ({ items }: { items: BreadcrumbItem[] }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: `${SEO_DEFAULTS.siteUrl}${item.url}`,
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

/**
 * FAQ Schema - pre stránky s FAQ
 */
export const FAQSchema = ({ items }: { items: FAQItem[] }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

/**
 * Local Business Schema
 */
export const LocalBusinessSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Store",
    name: SEO_DEFAULTS.siteName,
    url: SEO_DEFAULTS.siteUrl,
    image: `${SEO_DEFAULTS.siteUrl}/logos/merch-logo.png`,
    priceRange: "€€",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Bratislava",
      addressCountry: "SK",
    },
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "09:00",
      closes: "17:00",
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

// ============================================
// EXPORTS
// ============================================

export default SEOHead;
